import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { MapPin, Ticket } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";

// Beschreibung mit "Weiterlesen"-Aufklappen. Default 2 Zeilen (line-clamp-2),
// Toggle nur, wenn der Text tatsaechlich ueber 2 Zeilen hinausgeht (Overflow-
// Messung). whitespace-pre-line erhaelt Absaetze/Zeilenumbrueche.
const EventDescription = ({ text }) => {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        // Nur im geklappten Zustand messen; beim Aufklappen Wert nicht ueberschreiben.
        if (!el || expanded) return;
        const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, [text, expanded]);

    if (!text) return null;

    return (
        <div className="mb-4">
            <p
                ref={ref}
                className={`text-concrete-600 text-sm whitespace-pre-line ${expanded ? '' : 'line-clamp-2'}`}
            >
                {text}
            </p>
            {(overflowing || expanded) && (
                <button
                    type="button"
                    onClick={() => setExpanded(v => !v)}
                    className="mt-1 text-xs font-medium text-black underline hover:text-concrete-700"
                    aria-expanded={expanded}
                >
                    {expanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                </button>
            )}
        </div>
    );
};

export const EventList = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxImages, setLightboxImages] = useState(null);
    const { language } = useLanguage();

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/events`);
                // Filter today's and future events (compare date only, ignore time)
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const upcoming = response.data
                    .filter(e => {
                        const eventDate = new Date(e.date);
                        const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
                        return eventDay >= today;
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date));
                setEvents(upcoming);
            } catch (error) {
                console.error("Failed to fetch events", error);
            } finally {
                setLoading(false);
            }
        };
        fetchEvents();
    }, []);

    if (loading) return <div className="text-center p-8 text-concrete-500 font-mono">Loading...</div>;
    if (events.length === 0) return <div className="text-center p-8 text-concrete-500 italic">Currently no upcoming events.</div>;

    return (
        <>
            {lightboxImages && <Lightbox images={lightboxImages} onClose={() => setLightboxImages(null)} />}
            
            <div className="space-y-4 md:space-y-6" role="list" aria-label="Kommende Events">
                {events.map((event) => (
                    <div key={event.id} className="border border-concrete-200 rounded-sm hover:shadow-md transition-shadow group flex flex-col sm:flex-row sm:items-stretch">
                        <div className="bg-concrete-50 p-4 md:p-6 flex flex-row sm:flex-col items-center justify-between sm:justify-center min-w-[100px] sm:min-w-[120px] text-black gap-2 sm:gap-0 shrink-0">
                            <span className="text-2xl md:text-3xl font-bold font-heading">{new Date(event.date).getDate()}</span>
                            <div className="flex flex-col sm:items-center">
                                <span className="uppercase tracking-wider text-xs md:text-sm font-bold">{new Date(event.date).toLocaleString(language, { month: 'short' })}</span>
                                <span className="text-[10px] md:text-xs mt-0 sm:mt-1 text-concrete-600">{new Date(event.date).getFullYear()}</span>
                            </div>
                        </div>
                        <div className="p-4 md:p-6 flex-1 flex flex-col justify-between">
                            <div>
                                <h3 className="text-lg md:text-xl font-bold font-heading text-black mb-2">{event.title}</h3>
                                <EventDescription text={event.description} />
                            </div>
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-concrete-100">
                                <span className="flex items-center text-xs text-concrete-500 font-medium"><MapPin size={14} className="mr-1" /> Teamwerk</span>
                                {/* Ticket Button - links to internal ticket shop or external link */}
                                <div className="relative group/ticket">
                                    <Button 
                                        size="sm" 
                                        className={`rounded-none uppercase tracking-wider text-xs ${
                                            (event.ticket_link || event.has_tickets)
                                                ? 'bg-black hover:bg-concrete-800 text-white cursor-pointer' 
                                                : 'bg-concrete-300 text-concrete-500 cursor-not-allowed'
                                        }`}
                                        onClick={() => {
                                            if (event.ticket_link) {
                                                window.open(event.ticket_link, '_blank');
                                            } else if (event.has_tickets) {
                                                window.location.href = `/tickets/${event.id}`;
                                            }
                                        }}
                                        disabled={!event.ticket_link && !event.has_tickets}
                                    >
                                        Zu den Tickets <Ticket size={14} className="ml-2" />
                                    </Button>
                                    {/* Hover tooltip for grayed out button */}
                                    {!event.ticket_link && !event.has_tickets && (
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-red-600 text-white text-xs rounded shadow-lg opacity-0 group-hover/ticket:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                                            Vorverkauf startet ca. 6 Wochen vor Event-Termin!
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-red-600"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {event.flyer_url && (
                            <div className="flex items-center justify-center px-4 border-l border-concrete-100 bg-concrete-50">
                                <button
                                    onClick={() => setLightboxImages([event.flyer_url].filter(Boolean))}
                                    className="flex flex-col items-center gap-2 py-4 px-3 text-concrete-600 hover:text-black transition-colors"
                                    aria-label="Flyer anzeigen"
                                >
                                    <img src={event.flyer_url} alt={event.title} className="w-16 h-auto object-contain opacity-80 hover:opacity-100 transition-opacity" />
                                    <span className="text-xs font-medium">Flyer</span>
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </>
    );
};
